import { Box, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { useId, useState, type MouseEvent, type ReactNode } from "react";
import type { JSX } from "react";

/* ---------- Dot menu ------------------------------------------------ */

export type DotMenuItem =
  | {
      label: string;
      onSelect: () => void;
      icon?: ReactNode;
      tone?: "default" | "danger";
      disabled?: boolean;
      divider?: undefined;
    }
  | { divider: true };

/**
 * Three-dot overflow menu. MUI `Menu` provides click-outside / Escape /
 * focus management + ARIA; the trigger carries `aria-haspopup` /
 * `aria-expanded`. Same `DotMenuItem` contract as the old ui-legacy
 * adapter so callers are unchanged.
 */
export function DotMenu({
  label = "More actions",
  items,
}: {
  label?: string;
  items: ReadonlyArray<DotMenuItem>;
}): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = useId();
  const close = () => setAnchorEl(null);

  return (
    <>
      <IconButton
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        {...(open ? { "aria-controls": menuId } : {})}
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        sx={{
          height: 28,
          width: 28,
          color: "text.disabled",
          "&:hover": { bgcolor: "surfaceSoft", color: "text.primary" },
        }}
      >
        <Box component="svg" viewBox="0 0 16 16" sx={{ height: 16, width: 16 }} fill="currentColor" aria-hidden>
          <circle cx="3" cy="8" r="1.4" />
          <circle cx="8" cy="8" r="1.4" />
          <circle cx="13" cy="8" r="1.4" />
        </Box>
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 176, mt: 0.5 } } }}
        MenuListProps={{ "aria-label": label }}
      >
        {items.map((item, i) =>
          "divider" in item && item.divider ? (
            <Divider key={`hr-${i}`} />
          ) : (
            <MenuItem
              key={item.label}
              disabled={!!item.disabled}
              onClick={() => {
                close();
                item.onSelect();
              }}
              sx={{ fontSize: 14, ...(item.tone === "danger" ? { color: "error.main" } : {}) }}
            >
              {item.icon ? (
                <Box component="span" aria-hidden sx={{ mr: 1, display: "inline-flex" }}>
                  {item.icon}
                </Box>
              ) : null}
              <Box component="span">{item.label}</Box>
            </MenuItem>
          ),
        )}
      </Menu>
    </>
  );
}
